import { z } from 'zod';

/**
 * The approver-facing verdict for one pending voucher — the whole contract the
 * approval screen needs, and (unchanged) the payload a future Slack/Teams card
 * would carry. Deliberately NOT a score, a bucket, a severity, or a
 * recommendation: two states, and the broken rules named exactly as the source
 * registered them.
 *
 * Produced by the pure `@voll-intelligence/approval-triage` core, stitched onto
 * each voucher by the app-integration BFF, consumed by web-chat.
 */

/** One evaluated policy rule's machine status, as the source registers it.
 * Canonical here — `pending_approval_triage_view` and the triage engine both
 * consume this enum rather than re-declaring it. */
export const PolicyRuleStatusSchema = z.enum(['WITHIN', 'WITHOUT', 'NOT_APPLY']);
export type PolicyRuleStatus = z.infer<typeof PolicyRuleStatusSchema>;

/**
 * One rule the voucher broke, in the approver's locale.
 *
 * `ruleName` and `ledgerText` are VERBATIM from the source — never composed,
 * never translated, never substituted. Empty at the source becomes `null` and
 * the UI falls back to a generic label; it never invents replacement prose.
 *
 * `policyRuleId`/`concept` come from the canonical policy taxonomy, looked up
 * as DATA (never a regex over the name). Both `null` on a lookup miss, which is
 * expected while the taxonomy is incomplete — the screen still renders
 * `ruleName`. `policyRuleId` is what exception telemetry aggregates on: the
 * provisional `ruleId` is index-coupled and not summable across vouchers.
 *
 * `explanation` is the one COMPOSED field (#6306): a deterministic sentence in
 * the approver's locale — what was asked, what the policy allows, the gap —
 * built only from values the source registered (policy snapshot, quotation,
 * request date). `null` whenever the rule has no template or any input is
 * missing or ambiguous; the screen then shows `ledgerText`. Never generic prose.
 */
export const BrokenRuleSchema = z.object({
  /** Provisional identity: `${index}:${en.name}`. Retired once Phase B emits a
   * structured ruleId at the source. */
  ruleId: z.string(),
  /** Canonical taxonomy id (`air.bookingWindow`, `hotel.cap.daily`, …). */
  policyRuleId: z.string().nullable(),
  /** Manager-facing concept (Antecedência · Teto de preço · Cabine · …). */
  concept: z.string().nullable(),
  /** `descriptions[approverLocale].name`, verbatim. */
  ruleName: z.string().nullable(),
  /** `descriptions[approverLocale].description`, verbatim. */
  ledgerText: z.string().nullable(),
  /** The composed sentence, or `null`. Optional (the `obs` precedent) because
   * it may be ABSENT: a verdict from a voucher-events that predates the field
   * (deploy in any order) and a triage-cache doc written before it — the cache is
   * read raw from Mongo, never re-parsed. Consumers read absent as `null`. */
  explanation: z.string().nullable().optional(),
  status: PolicyRuleStatusSchema,
});
export type BrokenRule = z.infer<typeof BrokenRuleSchema>;

/**
 * Why the requester says the trip exists — the two picklist labels the source
 * captured at request time, verbatim — and which cost centers it is charged to.
 *
 * Deliberately NOT part of `ApprovalVerdict`: it is context, not a verdict, and
 * it is not derived from any policy evaluation. Practical consequence — it is
 * plucked from the live view on every read, so it never rides the triage cache
 * and can never go stale against it.
 *
 * `obs` (the requester's free text) crosses (#4667): it is where the requester
 * says what the request is about, and the legacy product already showed it to
 * the approver. It is trimmed and capped at the pluck, and never logged.
 */
export const ApprovalRequestContextSchema = z.object({
  /** `approvalContext.motive.name` (e.g. "DESPESAS VIAGENS"), verbatim. */
  motive: z.string().nullable(),
  /** `approvalContext.justification.name`, verbatim. */
  justification: z.string().nullable(),
  /** `approvalContext.obs`, trimmed and capped at 2000 characters. Optional so a
   * producer still on the shape without it stays valid. */
  obs: z.string().nullable().optional(),
  /** The cost allocation (`approvalContext.costCenters`), one row per split:
   * names and percent only — the accounting `externalCode` stays on the gated
   * route. Defaulted so a producer still on the two-label shape stays valid. */
  costCenters: z
    .array(
      z.object({
        company: z.string().nullable(),
        costCenter: z.string().nullable(),
        project: z.string().nullable(),
        percent: z.number().nullable(),
      }),
    )
    .default([]),
});
export type ApprovalRequestContext = z.infer<typeof ApprovalRequestContextSchema>;

/** Every field absent. The default for every producer that does not read the
 * mirror-only triage block. */
export const NO_REQUEST_CONTEXT: ApprovalRequestContext = {
  motive: null,
  justification: null,
  obs: null,
  costCenters: [],
};

/**
 * Why the voucher needs attention — or `OK`. `UNTRIAGED` is the fail-soft
 * outcome (triage unreachable/absent): it is deliberately an attention state,
 * because a voucher must NEVER read as compliant by omission.
 */
export const ApprovalVerdictReasonSchema = z.enum([
  'OK',
  'POLICY_VIOLATION',
  'WEAK_POLICY',
  'NO_QUOTATION',
  'UNTRIAGED',
]);
export type ApprovalVerdictReason = z.infer<typeof ApprovalVerdictReasonSchema>;

export const ApprovalVerdictSchema = z.object({
  needsAttention: z.boolean(),
  reason: ApprovalVerdictReasonSchema,
  /** Only `WITHOUT` rows — the server filters, so no consumer can render a
   * satisfied rule as an exception. Empty for every non-violation reason. */
  brokenRules: z.array(BrokenRuleSchema),
});
export type ApprovalVerdict = z.infer<typeof ApprovalVerdictSchema>;

/** The fail-soft verdict. Used as the schema default and by the BFF when the
 * triage read is unavailable — attention, never OK. */
export const UNTRIAGED_VERDICT: ApprovalVerdict = {
  needsAttention: true,
  reason: 'UNTRIAGED',
  brokenRules: [],
};

/**
 * A voucher on the same trip that is NOT awaiting this approver's decision —
 * rendered read-only so the approver sees the whole trip, not just the slice
 * asking for them.
 *
 * Deliberately NOT called "decided". The set is everything past
 * `AWAITING_APPROVAL`, which includes IN_PROGRESS: on the customer mirror that
 * is 1143 of 2754 such vouchers, so "decided" would be wrong for roughly 40% of
 * these rows. Each row carries its own `status` and the UI labels it from that.
 *
 * Never carries a verdict — only the pending set goes through triage.
 */
export const OtherTripVoucherSchema = z.object({
  voucherId: z.string(),
  voucherCode: z.string().nullable(),
  modal: z.string().nullable(),
  supplier: z.string().nullable(),
  amount: z.number().nullable(),
  currency: z.string().nullable(),
  /** Canonical `VoucherStatus` (CONFIRMED/DECLINED/…), already mapped. */
  status: z.string().nullable(),
  /**
   * ISO instant the voucher reached this status: the concluding
   * `actions.approved[].at`, else `emittedAt`. `null` when the mirror has
   * neither — the row still renders, just undated.
   *
   * Availability is very uneven by status (measured on the mirror): CONFIRMED
   * 99%, IN_PROGRESS 28%, CANCELLED 5%, EXPIRED 1%, DECLINED 0%. A consumer must
   * treat the date as optional decoration, never as the row's identity.
   */
  decidedAt: z.string().nullable(),
  /**
   * WHO travels on this voucher. A multi-passenger booking puts each
   * passenger's voucher on the same trip: without the owner, a companion's
   * ticket at the same price reads as the traveler's own charged twice (#4102).
   * `id` is the opaque publicId — the pending list's `travelerId` — so a
   * consumer tells owners apart by id, never by name. Only id + name: no email,
   * approver, or externalCode. `null` = the voucher has no traveler; absent = a
   * producer predating the field. Either way the row renders without an owner.
   */
  traveler: z.object({ id: z.string(), name: z.string().nullable() }).nullable().optional(),
});
export type OtherTripVoucher = z.infer<typeof OtherTripVoucherSchema>;
